import { Injectable } from '@angular/core';
import {
  ProcessErrorCodeAdapterService,
  ProcessErrorCodeFn,
} from 'src/app/components/scrolling-list/services/process-error-code-adapter.service';
import { ModalService } from 'src/app/components/modals/modal.service';
import { CurrentPlanService } from 'src/app/components/plans/current-plan.service';

import { mergeMap, Subject, tap } from 'rxjs';
import { CompanyApiService } from 'src/app/api/services/company-api.service';
import { type Display } from 'src/app/api/services/display';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { PermissionService } from 'src/app/auth/services/permission.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { DISPLAY_ACCESS } from 'src/app/auth/types/access-level';

type UpdateLicenseParams = {
  display: Display;
  success: () => void;
  error: (error: string) => void;
  licenseType:
    | typeof PlayerLicenseService.DISPLAY_LICENSE_TYPE
    | typeof PlayerLicenseService.INTERACTIVE_LICENSE_TYPE;
};

@Injectable({
  providedIn: 'root',
})
export class PlayerLicenseService {
  static readonly DISPLAY_LICENSE_TYPE = 'display';
  static readonly INTERACTIVE_LICENSE_TYPE = 'interactive';

  private updateLicenseSubject = new Subject<UpdateLicenseParams>();

  apiError = '';
  updatingLicense = false;

  private processErrorCode: ProcessErrorCodeFn;

  constructor(
    private currentPlanService: CurrentPlanService,
    private userStateService: UserStateService,
    private permissionService: PermissionService,
    private companyStateService: CompanyStateService,
    private companyApiService: CompanyApiService,
    processErrorCodeAdapter: ProcessErrorCodeAdapterService,
    private modalService: ModalService,
  ) {
    this.processErrorCode = processErrorCodeAdapter.createCallable();

    this.initializeUpdateLicense();
  }

  initializeUpdateLicense() {
    this.updateLicenseSubject
      .pipe(
        tap(() => (this.updatingLicense = true)),
        mergeMap(({ display, success, error, licenseType }) => {
          if (licenseType === PlayerLicenseService.DISPLAY_LICENSE_TYPE) {
            return this.updateDisplayLicense(display)
              .then(success)
              .catch((e) => error(e));
          } else if (
            licenseType === PlayerLicenseService.INTERACTIVE_LICENSE_TYPE
          ) {
            return this.updateDisplayInteractive(display)
              .then(success)
              .catch((e) => error(e));
          }
        }),
      )
      .subscribe(() => (this.updatingLicense = false));
  }

  updateLicense(params: UpdateLicenseParams) {
    this.updateLicenseSubject.next(params);
  }

  getUsedLicenseString(): string {
    let text =
      this.getProUsedLicenseCount() +
      ' Licensed Display' +
      (this.getProUsedLicenseCount() !== 1 ? 's' : '') +
      ' / ';
    if (this.currentPlanService.isUnlimitedPlan()) {
      text += 'Unlimited Licenses';
    } else {
      text +=
        this.getProAvailableLicenseCount() +
        ' Available License' +
        (this.getProAvailableLicenseCount() !== 1 ? 's' : '');
    }
    return text;
  }

  getUsedLicenseCountString() {
    return `
      ${this.getProUsedLicenseCount()}/${
        this.currentPlanService.isUnlimitedPlan()
          ? 'Unlimited'
          : this.getProAvailableLicenseCount()
      }`;
  }

  getAvailableLicenseCountString() {
    return this.currentPlanService.isUnlimitedPlan()
      ? 'Unlimited'
      : this.getProAvailableLicenseCount() + '/' + this.getProLicenseCount();
  }

  isProAvailable(display: any): boolean {
    return (
      this.hasProfessionalLicenses() &&
      this.getProLicenseCount() > 0 &&
      !this.areAllProLicensesUsed(display)
    );
  }

  isInteractiveAvailable(display: any): boolean {
    return (
      this.hasInteractiveLicenses() &&
      this.getInteractiveLicenseCount() > 0 &&
      !this.areAllInteractiveLicensesUsed(display)
    );
  }

  hasInteractiveLicenses(): boolean {
    return this.currentPlanService.currentPlan.interactiveTotalLicenseCount > 0;
  }

  getInteractiveLicenseCount(): number {
    return (
      this.currentPlanService.currentPlan.interactiveTotalLicenseCount || 0
    );
  }

  getInteractiveAvailableLicenseCount(): number {
    return (
      this.currentPlanService.currentPlan.interactiveAvailableLicenseCount || 0
    );
  }

  getInteractiveUsedLicenseCount(): number {
    return (
      this.getInteractiveLicenseCount() -
      this.getInteractiveAvailableLicenseCount()
    );
  }

  areAllInteractiveLicensesUsed(display: any): boolean {
    const allLicensesUsed = !this.getInteractiveAvailableLicenseCount();
    const allInteractiveLicensesUsed =
      allLicensesUsed && !(display && display.interactiveAssigned);

    return this.getInteractiveLicenseCount() > 0 && allInteractiveLicensesUsed;
  }

  isInteractiveToggleEnabled(display: any): boolean {
    return (
      this.userStateService.hasRole('sa') &&
      this.isInteractiveAvailable(display)
    );
  }

  hasProfessionalLicenses(): boolean {
    return this.currentPlanService.currentPlan.playerProTotalLicenseCount > 0;
  }

  getProLicenseCount(): number {
    return this.currentPlanService.currentPlan.playerProTotalLicenseCount || 0;
  }

  getProAvailableLicenseCount(): number {
    return (
      this.currentPlanService.currentPlan.playerProAvailableLicenseCount || 0
    );
  }

  getProUsedLicenseCount(): number {
    return this.getProLicenseCount() - this.getProAvailableLicenseCount();
  }

  isProToggleEnabled(display: any): boolean {
    return (
      !this.currentPlanService.isUnlimitedPlan() &&
      this.permissionService.hasAccess(
        DISPLAY_ACCESS.FULL_ACCESS,
        display?.id,
      ) &&
      ((display && display.playerProAuthorized) ||
        (this.areAllProLicensesUsed(display)
          ? !this.currentPlanService.currentPlan.isPurchasedByParent
          : true))
    );
  }

  areAllProLicensesUsed(display: any): boolean {
    const allLicensesUsed = !this.getProAvailableLicenseCount();
    const allProLicensesUsed =
      allLicensesUsed && !(display && display.playerProAssigned);

    return this.getProLicenseCount() > 0 && allProLicensesUsed;
  }

  updateDisplayLicenseLocal(display: any): void {
    const playerProAuthorized = display.playerProAuthorized;
    const company = this.companyStateService.getCopyOfSelectedCompany(true);

    display.playerProAssigned = playerProAuthorized;
    display.playerProAuthorized =
      (display.originalPlayerProAuthorized ||
        company.playerProAvailableLicenseCount > 0) &&
      playerProAuthorized;
  }

  updateDisplayInteractiveLocal(display: any): void {
    const interactiveAuthorized = display.interactiveAuthorized;
    const company = this.companyStateService.getCopyOfSelectedCompany(true);
    let availableLicenseCount = company.interactiveAvailableLicenseCount || 0;

    display.interactiveAssigned = interactiveAuthorized;
    display.interactiveAuthorized =
      (display.originalInteractiveAuthorized || availableLicenseCount > 0) &&
      interactiveAuthorized;
  }

  toggleInteractiveLicenseLocal(
    interactiveAuthorized: boolean,
    displaysCount?: number,
  ): void {
    const count = displaysCount || 1;
    const company = this.companyStateService.getCopyOfSelectedCompany(true);
    let availableLicenseCount = company.interactiveAvailableLicenseCount || 0;

    if (interactiveAuthorized) {
      availableLicenseCount = availableLicenseCount - count;
      availableLicenseCount =
        availableLicenseCount < 0 ? 0 : availableLicenseCount;
    } else {
      availableLicenseCount = availableLicenseCount + count;
    }

    company.interactiveAvailableLicenseCount = availableLicenseCount;
    this.companyStateService.updateCompanySettings(company);
  }

  updateDisplayInteractive(display: Display): Promise<any> {
    this.updatingLicense = true;
    const interactive = display.interactiveAuthorized;
    const apiParams = {};
    apiParams[display.id] = interactive;
    return this.companyApiService
      .enableCompanyProduct(
        display.companyId,
        PlayerLicenseService.INTERACTIVE_LICENSE_TYPE,
        apiParams,
      )
      .then((resp) => {
        const resultDisplays = resp && resp.item && resp.item.displays;
        if (
          resultDisplays &&
          resultDisplays[display.id] !== apiParams[display.id]
        ) {
          display.interactiveAuthorized = resultDisplays[display.id];
          this.updateDisplayInteractiveLocal(display);
          throw new Error('License could not be updated. Please try again.');
        }
      })
      .catch((e) => {
        this.apiError = this.processErrorCode(e);
        console.error(this.apiError, e);
        return Promise.reject(e);
      })
      .finally(() => {
        this.updatingLicense = false;
      });
  }

  updateDisplayLicense(display: Display): Promise<any> {
    this.apiError = '';
    this.updatingLicense = true;

    const apiParams = {};
    const playerProAuthorized = display.playerProAuthorized;
    apiParams[display.id] = playerProAuthorized;

    return this.companyApiService
      .enableCompanyProduct(
        display.companyId,
        PlayerLicenseService.DISPLAY_LICENSE_TYPE,
        apiParams,
      )
      .then((resp) => {
        const resultDisplays = resp && resp.item && resp.item.displays;
        if (
          resultDisplays &&
          resultDisplays[display.id] === apiParams[display.id]
        ) {
          this.updateDisplayLicenseLocal(display);
        } else {
          throw new Error('License could not be updated. Please try again.');
        }
      })
      .catch((e) => {
        this.apiError = this.processErrorCode(e);
        console.error(this.apiError, e);
        return Promise.reject(e);
      })
      .finally(() => {
        this.updatingLicense = false;
        if (!playerProAuthorized) {
          display.monitoringEnabled = false;
        }
      });
  }

  toggleDisplayLicenseLocal(
    playerProAuthorized: boolean,
    displaysCount?: number,
  ): void {
    const count = displaysCount || 1;
    const company = this.companyStateService.getCopyOfSelectedCompany(true);
    let availableLicenseCount = company.playerProAvailableLicenseCount || 0;

    if (playerProAuthorized) {
      availableLicenseCount = availableLicenseCount - count;
      availableLicenseCount =
        availableLicenseCount < 0 ? 0 : availableLicenseCount;
    } else {
      availableLicenseCount = availableLicenseCount + count;
    }

    company.playerProAvailableLicenseCount = availableLicenseCount;
    this.companyStateService.updateCompanySettings(company);
  }

  confirmAndLicense(displayIds: string[]): Promise<any> {
    const countText =
      displayIds.length + ' display' + (displayIds.length > 1 ? 's' : '');
    return this.modalService
      .confirm(
        'License ' + countText + '?',
        'You are about to assign licenses to ' +
          countText +
          '. Would you like to proceed?',
        'Yes',
        'No',
      )
      .then(() => {
        if (this.getProAvailableLicenseCount() >= displayIds.length) {
          return this._licenseDisplays(displayIds);
        } else {
          this.currentPlanService.confirmAndPurchase(
            displayIds.length - this.getProAvailableLicenseCount(),
          );
          return Promise.reject();
        }
      });
  }

  licenseDisplaysByCompanyId(
    companyId: string,
    displayIds: string[],
  ): Promise<any> {
    const apiParams = {};
    for (let i = 0; i < displayIds.length; i++) {
      apiParams[displayIds[i]] = true;
    }

    return this.companyApiService
      .enableCompanyProduct(
        companyId,
        PlayerLicenseService.DISPLAY_LICENSE_TYPE,
        apiParams,
      )
      .then((resp) => {
        const resultDisplays = resp && resp.item && resp.item.displays;
        let licensedCount = 0;
        for (let i = 0; i < displayIds.length; i++) {
          if (resultDisplays && resultDisplays[displayIds[i]] === true) {
            licensedCount++;
          }
        }

        this.toggleDisplayLicenseLocal(true, licensedCount);

        if (licensedCount !== displayIds.length) {
          throw new Error('License could not be updated. Please try again.');
        }
      });
  }

  private _licenseDisplays(displayIds: string[]): Promise<any> {
    this.apiError = '';
    this.updatingLicense = true;

    return this.licenseDisplaysByCompanyId(
      this.companyStateService.getSelectedCompanyId(),
      displayIds,
    )
      .catch((e) => {
        this.apiError = this.processErrorCode(e);
        console.error(this.apiError, e);
        return Promise.reject(e);
      })
      .finally(() => {
        this.updatingLicense = false;
      });
  }
}
